<script lang="ts">
  /**
   * The deep ground a game's big moments stand on: a full screen in the deep
   * gradient (`--ui-deep-from` to `--ui-deep-to`), words in `--ui-on-deep`,
   * one centred column that scrolls inside the screen when it must, and a
   * layer behind it for the game's own `art` (confetti, a drifting pattern).
   * `ResultsTemplate` is built on it.
   *
   * The art layer is decoration: `aria-hidden`, and it never takes a tap.
   *
   * Test id: `testid` on the screen (no default).
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly children: Snippet;
    readonly art?: Snippet | undefined;
    readonly testid?: string | undefined;
  }
  const { children, art, testid }: Props = $props();
</script>

<div class="ui-deep" data-testid={testid}>
  {#if art !== undefined}
    <div class="ui-deep-art" aria-hidden="true">{@render art()}</div>
  {/if}
  <div class="ui-deep-scroll">
    <div class="ui-deep-column">{@render children()}</div>
  </div>
</div>

<style>
  .ui-deep {
    position: relative;
    box-sizing: border-box;
    height: 100svh;
    overflow: hidden;
    background: linear-gradient(180deg, var(--ui-deep-from), var(--ui-deep-to));
    color: var(--ui-on-deep);
    font-family: var(--ui-font);
  }

  .ui-deep-art {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  }

  .ui-deep-scroll {
    position: relative;
    z-index: 1;
    box-sizing: border-box;
    height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
      env(safe-area-inset-left);
  }

  .ui-deep-column {
    box-sizing: border-box;
    flex: 1 0 auto;
    width: min(26rem, 100%);
    margin-inline: auto;
    display: flex;
    flex-direction: column;
    justify-content: safe center;
    padding: 2.5rem 1.5rem 2rem;
    text-align: center;
  }
</style>
